import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import CallButton from '../components/CallButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { CalendarDays, Plus, Trash2, Clock, Phone, User, ChevronLeft, ChevronRight, Edit2, Loader2, MessageCircle, MapPin, Briefcase, Users, Link2, Settings2 } from 'lucide-react';
import { format, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isToday, isSameMonth } from 'date-fns';
import { it } from 'date-fns/locale';
import { AgendaTeam } from '../components/booking/AgendaTeam';
import { OrariDialog } from '../components/booking/OrariDialog';
import { RichiestePanel } from '../components/booking/RichiestePanel';

const MOTIVI = [
  'Polizza azienda', 'Piano previdenziale complementare', 'PIP',
  'Finanziamento CQP/CQS', 'Finanziamento PP', 'Polizza Casa',
  'Assicurazione salute/Infortuni/Vita', 'Consulenza generica', 'Altro'
];
const CONTATTI = ['Chiamata', 'Email', 'Di presenza', 'WhatsApp'];
const ORARI = Array.from({ length: 20 }, (_, i) => { const h = 8 + Math.floor(i / 2); const m = i % 2 === 0 ? '00' : '30'; return `${h.toString().padStart(2, '0')}:${m}`; });

const MOTIVO_COLORS = {
  'Polizza azienda': 'bg-blue-500',
  'Piano previdenziale complementare': 'bg-violet-500',
  'PIP': 'bg-purple-500',
  'Finanziamento CQP/CQS': 'bg-teal-500',
  'Finanziamento PP': 'bg-cyan-500',
  'Polizza Casa': 'bg-amber-500',
  'Assicurazione salute/Infortuni/Vita': 'bg-rose-500',
  'Consulenza generica': 'bg-slate-500',
  'Altro': 'bg-slate-400',
};

const CONTATTO_ICONS = {
  'Chiamata': Phone,
  'Email': () => <span className="text-xs">@</span>,
  'Di presenza': MapPin,
  'WhatsApp': MessageCircle,
};

const emptyForm = { titolo: '', data: '', ora: '', nome_cliente: '', telefono: '', email_cliente: '', motivo: '', note: '', contatto: '', assigned_to: '' };

export const Calendario = () => {
  const { user } = useAuth();
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [callingId, setCallingId] = useState(null);
  const [whatsappingId, setWhatsappingId] = useState(null);
  const [vista, setVista] = useState('calendario');
  const [orariOpen, setOrariOpen] = useState(false);
  const [richieste, setRichieste] = useState([]);
  const [riceventi, setRiceventi] = useState(null);

  useEffect(() => { fetchAppuntamenti(); fetchUsers(); fetchRichieste(); fetchRiceventi(); }, []);

  const fetchRiceventi = async () => {
    try { const r = await api.get('/booking/riceventi'); setRiceventi(r.data.user_ids || []); } catch {}
  };

  const handleOrariClose = (open) => { setOrariOpen(open); if (!open) fetchRiceventi(); };

  const fetchRichieste = async () => {
    try { const r = await api.get('/booking/richieste'); setRichieste(r.data); } catch {}
  };

  const copiaLinkPrenotazioni = () => {
    const link = `${window.location.origin}/prenota`;
    navigator.clipboard.writeText(link).then(
      () => toast.success('Link copiato! Condividilo con i tuoi clienti: ' + link),
      () => toast.error('Impossibile copiare il link')
    );
  };

  const fetchAppuntamenti = async () => {
    try { const r = await api.get('/calendario'); setAppuntamenti(r.data); } catch {}
  };
  const fetchUsers = async () => {
    try { const r = await api.get('/users'); setUsers(r.data); } catch {}
  };

  const todayApps = useMemo(() => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    return appuntamenti.filter(a => a.data === dateStr).sort((a, b) => (a.ora || '').localeCompare(b.ora || ''));
  }, [appuntamenti, selectedDate]);

  const daysWithApps = useMemo(() => {
    const map = {};
    appuntamenti.forEach(a => {
      if (!map[a.data]) map[a.data] = 0;
      map[a.data]++;
    });
    return map;
  }, [appuntamenti]);

  const openNew = () => { setEditingApp(null); setForm({ ...emptyForm, data: format(selectedDate, 'yyyy-MM-dd') }); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditingApp(a);
    setForm({
      titolo: a.titolo || '', data: a.data || '', ora: a.ora || '',
      nome_cliente: a.nome_cliente || '', telefono: a.telefono || '',
      email_cliente: a.email_cliente || '', motivo: a.motivo || '',
      note: a.note || '', contatto: a.contatto || '', assigned_to: a.assigned_to || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.titolo || !form.data) { toast.error('Titolo e data sono obbligatori'); return; }
    try {
      const params = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, v); });
      if (editingApp) {
        await api.put(`/calendario/${editingApp.id}?${params.toString()}`);
        toast.success('Appuntamento aggiornato');
      } else {
        await api.post(`/calendario?${params.toString()}`);
        toast.success('Appuntamento creato');
      }
      setDialogOpen(false); setEditingApp(null); fetchAppuntamenti();
    } catch (err) { toast.error('Errore: ' + (err?.response?.data?.detail || 'Riprova')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    try { await api.delete(`/calendario/${id}`); fetchAppuntamenti(); } catch {}
  };

  const sendManualReminder = async (app) => {
    setSendingReminder(app.id);
    try {
      const res = await api.post(`/reminders/send-manual/${app.id}`);
      toast.success(res.data.message || 'Reminder inviato!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio'); }
    finally { setSendingReminder(null); }
  };

  const handleVoipCall = async (app) => {
    if (!app.telefono) return;
    setCallingId(app.id);
    try {
      await api.post('/centralino/call', { number: app.telefono, description: app.nome_cliente || app.titolo || '' });
      toast.success('Chiamata avviata! Il tuo telefono sta squillando...');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore chiamata centralino'); }
    finally { setCallingId(null); }
  };

  const handleWhatsApp = async (app) => {
    if (!app.telefono) return;
    setWhatsappingId(app.id);
    let clean = app.telefono.replace(/[\s\-()]/g, '');
    if (clean.startsWith('+')) clean = clean.slice(1);
    if (!clean.startsWith('39')) clean = '39' + clean;
    if (clean.startsWith('390')) clean = '39' + clean.slice(3);
    const chatId = `${clean}@c.us`;
    try {
      await api.post('/whatsapp/send', { numero: chatId, messaggio: `Buongiorno ${app.nome_cliente || ''}, la contatto per il suo appuntamento del ${app.data}${app.ora ? ' ore ' + app.ora : ''}. F.lli Schiliro - 0957938069` });
      toast.success(`WhatsApp inviato a ${app.nome_cliente || app.telefono}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio WhatsApp'); }
    finally { setWhatsappingId(null); }
  };

  const F = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const start = startOfMonth(currentMonth);
  const end = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start, end });
  const startDay = start.getDay() === 0 ? 6 : start.getDay() - 1;

  const getBarColor = (dateStr) => {
    const count = daysWithApps[dateStr] || 0;
    if (count >= 5) return 'bg-amber-500';
    if (count >= 3) return 'bg-amber-400';
    if (count >= 1) return 'bg-blue-400';
    return '';
  };

  return (
    <div className="p-4 md:p-6 min-h-screen" data-testid="calendario-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Calendario</h1>
          <p className="text-sm text-slate-500 mt-0.5">Gestisci i tuoi appuntamenti</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-white border border-slate-200 rounded-lg p-0.5">
            <button onClick={() => setVista('calendario')} data-testid="vista-calendario"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${vista === 'calendario' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
              <CalendarDays size={13} /> Calendario
            </button>
            <button onClick={() => setVista('team')} data-testid="vista-team"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${vista === 'team' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
              <Users size={13} /> Agenda Team
            </button>
          </div>
          <Button onClick={copiaLinkPrenotazioni} size="sm" variant="outline" className="text-xs" data-testid="copia-link-btn">
            <Link2 size={13} className="mr-1.5" /> <span className="hidden sm:inline">Copia link prenotazioni</span><span className="sm:hidden">Link</span>
          </Button>
          <Button onClick={() => setOrariOpen(true)} size="sm" variant="outline" className="text-xs" data-testid="orari-btn">
            <Settings2 size={13} className="mr-1.5" /> <span className="hidden sm:inline">I miei orari</span>
          </Button>
          <Button onClick={openNew} size="sm" className="bg-slate-800 hover:bg-slate-700 text-white shadow-sm" data-testid="new-appuntamento-btn">
            <Plus size={16} className="mr-1.5" /> <span className="hidden sm:inline">Nuovo Appuntamento</span><span className="sm:hidden">Nuovo</span>
          </Button>
        </div>
      </div>

      <RichiestePanel richieste={richieste} onReload={() => { fetchRichieste(); fetchAppuntamenti(); }} />

      {vista === 'team' && (
        <div className="mb-5 space-y-4">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 w-fit">
            <button onClick={() => setSelectedDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; })} className="p-1 hover:bg-slate-100 rounded"><ChevronLeft size={14} /></button>
            <input type="date" value={format(selectedDate, 'yyyy-MM-dd')} onChange={e => e.target.value && setSelectedDate(new Date(e.target.value))}
              className="text-sm border-0 focus:outline-none bg-transparent" data-testid="team-date-input" />
            <button onClick={() => setSelectedDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; })} className="p-1 hover:bg-slate-100 rounded"><ChevronRight size={14} /></button>
          </div>
          <AgendaTeam selectedDate={selectedDate} />
        </div>
      )}

      {vista === 'calendario' && (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-5">
        {/* ─── Calendar Grid ─── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Month navigation */}
          <div className="flex items-center justify-between px-5 py-4 bg-slate-800 text-white">
            <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
              <ChevronLeft size={18} />
            </button>
            <h2 className="text-sm sm:text-base font-bold capitalize tracking-wide">{format(currentMonth, 'MMMM yyyy', { locale: it })}</h2>
            <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="p-3 sm:p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((d, i) => (
                <div key={i} className="text-[10px] sm:text-[11px] font-bold text-slate-400 text-center py-1 uppercase tracking-wider">{d}</div>
              ))}
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7 gap-1">
              {Array(startDay).fill(null).map((_, i) => <div key={`e-${i}`} className="aspect-square" />)}
              {days.map(day => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const count = daysWithApps[dateStr] || 0;
                const isSelected = isSameDay(day, selectedDate);
                const isTodayD = isToday(day);
                const inMonth = isSameMonth(day, currentMonth);
                const barColor = getBarColor(dateStr);

                return (
                  <button key={dateStr} onClick={() => setSelectedDate(day)} data-testid={`cal-day-${dateStr}`}
                    className={`relative aspect-square flex flex-col items-center justify-center rounded-xl text-xs sm:text-sm transition-all duration-150 ${
                      isSelected
                        ? 'bg-slate-800 text-white font-bold ring-2 ring-slate-800 ring-offset-2'
                        : isTodayD
                        ? 'bg-amber-50 text-amber-700 font-semibold border border-amber-200'
                        : inMonth
                        ? 'text-slate-700 hover:bg-slate-100'
                        : 'text-slate-300'
                    }`}>
                    <span>{day.getDate()}</span>
                    {count > 0 && (
                      <div className="flex items-center gap-0.5 mt-0.5">
                        <div className={`h-1 rounded-full ${barColor} ${count >= 3 ? 'w-4' : count >= 2 ? 'w-3' : 'w-2'}`} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ─── Day Detail Panel ─── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          {/* Day header */}
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  {format(selectedDate, 'EEEE', { locale: it })}
                </p>
                <h3 className="text-xl font-bold text-slate-900">
                  {format(selectedDate, 'dd MMMM yyyy', { locale: it })}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs font-semibold">{todayApps.length}</Badge>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-700" onClick={openNew} data-testid="add-app-day">
                  <Plus size={16} />
                </Button>
              </div>
            </div>
          </div>

          {/* Appointments list */}
          <div className="flex-1 overflow-y-auto">
            {todayApps.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 px-4">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                  <CalendarDays size={24} className="text-slate-300" />
                </div>
                <p className="text-sm text-slate-400 font-medium">Nessun appuntamento</p>
                <p className="text-xs text-slate-300 mt-1">Clicca + per aggiungerne uno</p>
              </div>
            ) : (
              <div className="p-3 space-y-2">
                {todayApps.map(a => {
                  const accentColor = MOTIVO_COLORS[a.motivo] || 'bg-slate-400';
                  const ContattoIcon = CONTATTO_ICONS[a.contatto];
                  return (
                    <div key={a.id} className="group relative rounded-xl border border-slate-100 bg-white hover:border-slate-200 hover:shadow-md transition-all duration-200 overflow-hidden" data-testid={`app-${a.id}`}>
                      {/* Color accent bar */}
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${accentColor} rounded-l-xl`} />

                      <div className="pl-4 pr-3 py-3">
                        {/* Top row: time + title + actions */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {a.ora && (
                                <span className="text-xs font-mono font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded-md">
                                  {a.ora}
                                </span>
                              )}
                              <h4 className="text-sm font-semibold text-slate-800 truncate">{a.titolo}</h4>
                            </div>

                            {/* Client info */}
                            {a.nome_cliente && (
                              <div className="flex items-center gap-1.5 mb-1">
                                <User size={12} className="text-slate-400" />
                                <span className="text-xs text-slate-600 font-medium">{a.nome_cliente}</span>
                              </div>
                            )}

                            {/* Meta row */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {a.telefono && (
                                <span className="text-[11px] text-slate-400 flex items-center gap-0.5">
                                  <Phone size={10} /> {a.telefono}
                                </span>
                              )}
                              {a.motivo && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full text-white font-medium ${accentColor}`}>
                                  {a.motivo.length > 20 ? a.motivo.substring(0, 20) + '...' : a.motivo}
                                </span>
                              )}
                              {ContattoIcon && (
                                <span className="text-[11px] text-slate-400 flex items-center gap-0.5 bg-slate-50 px-1.5 py-0.5 rounded-full">
                                  {typeof ContattoIcon === 'function' && ContattoIcon.prototype ? <ContattoIcon size={10} /> : <ContattoIcon />}
                                  <span className="ml-0.5">{a.contatto}</span>
                                </span>
                              )}
                            </div>

                            {a.assigned_to_nome && (
                              <p className="text-[11px] text-blue-500 font-medium mt-1 flex items-center gap-1">
                                <Briefcase size={10} /> {a.assigned_to_nome}
                              </p>
                            )}
                            {a.note && <p className="text-[11px] text-slate-400 mt-1.5 line-clamp-2">{a.note}</p>}
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                            {a.telefono && (
                              <CallButton number={a.telefono} description={a.nome_cliente || a.titolo || ''} className="p-1.5 rounded-lg text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors" />
                            )}
                            {a.telefono && (
                              <button onClick={() => handleWhatsApp(a)} disabled={whatsappingId === a.id}
                                className="p-1.5 rounded-lg text-slate-300 hover:text-emerald-500 hover:bg-emerald-50 transition-colors" title="Invia WhatsApp" data-testid={`wa-app-${a.id}`}>
                                {whatsappingId === a.id ? <Loader2 size={13} className="animate-spin" /> : <MessageCircle size={13} />}
                              </button>
                            )}
                            <button onClick={() => openEdit(a)} className="p-1.5 rounded-lg text-slate-300 hover:text-blue-500 hover:bg-blue-50 transition-colors" data-testid={`edit-app-${a.id}`}>
                              <Edit2 size={13} />
                            </button>
                            <button onClick={() => handleDelete(a.id)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Orari Dialog */}
      <OrariDialog open={orariOpen} onOpenChange={handleOrariClose} />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg">{editingApp ? 'Modifica Appuntamento' : 'Nuovo Appuntamento'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-[1fr_auto] gap-3">
              <div><Label className="text-xs font-medium text-slate-600">Titolo *</Label><Input value={form.titolo} onChange={(e) => F('titolo', e.target.value)} placeholder="Es. Incontro con cliente" className="mt-1" data-testid="app-titolo" /></div>
              <div className="w-28">
                <Label className="text-xs font-medium text-slate-600">Ora</Label>
                <Select value={form.ora} onValueChange={(v) => F('ora', v)}>
                  <SelectTrigger className="mt-1" data-testid="app-ora"><SelectValue placeholder="Ora" /></SelectTrigger>
                  <SelectContent>{ORARI.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-medium text-slate-600">Data *</Label><Input type="date" value={form.data} onChange={(e) => F('data', e.target.value)} className="mt-1" data-testid="app-data" /></div>
              <div><Label className="text-xs font-medium text-slate-600">Nome Cliente</Label><Input value={form.nome_cliente} onChange={(e) => F('nome_cliente', e.target.value)} placeholder="Nome completo" className="mt-1" data-testid="app-cliente" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-medium text-slate-600">Telefono</Label><Input value={form.telefono} onChange={(e) => F('telefono', e.target.value)} placeholder="+39..." className="mt-1" data-testid="app-tel" /></div>
              <div><Label className="text-xs font-medium text-slate-600">Email</Label><Input value={form.email_cliente} onChange={(e) => F('email_cliente', e.target.value)} placeholder="email@..." className="mt-1" data-testid="app-email" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">Assegna a</Label>
                <Select value={form.assigned_to} onValueChange={(v) => F('assigned_to', v)}>
                  <SelectTrigger className="mt-1" data-testid="app-assigned-to"><SelectValue placeholder="Utente" /></SelectTrigger>
                  <SelectContent>{users.filter(u => !riceventi || riceventi.includes(u.id)).map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Motivo</Label>
                <Select value={form.motivo} onValueChange={(v) => F('motivo', v)}>
                  <SelectTrigger className="mt-1" data-testid="app-motivo"><SelectValue placeholder="Motivo" /></SelectTrigger>
                  <SelectContent>{MOTIVI.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Contatto</Label>
                <Select value={form.contatto} onValueChange={(v) => F('contatto', v)}>
                  <SelectTrigger className="mt-1" data-testid="app-contatto"><SelectValue placeholder="Tipo" /></SelectTrigger>
                  <SelectContent>{CONTATTI.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs font-medium text-slate-600">Note</Label><Textarea value={form.note} onChange={(e) => F('note', e.target.value)} rows={2} placeholder="Note..." className="mt-1" data-testid="app-note" /></div>
            <Button onClick={handleSave} className="w-full bg-slate-800 hover:bg-slate-700 text-white" data-testid="create-app-btn">
              {editingApp ? 'Aggiorna Appuntamento' : 'Crea Appuntamento'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
